import React, { useMemo, useState } from 'react'
import { Row, Col, Card } from 'react-bootstrap'
import { BarChart, PieChart, TrendingUp, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { useClassDistribution, useAnnotationStats } from '../../../../store/hooks'
import { useGetProjectQuery } from '../../../../services/projectsApi'
import { ViewContainer, ScrollableContent } from '../../DatasetOverview/styles'
import styled from 'styled-components'

const ChartWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 2rem;
`

const AnalysisCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #fff;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .chart-container {
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const MetricItem = styled.div`
  background: linear-gradient(145deg, #1a202c 0%, #2d3748 100%);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.5);
    transform: translateY(-1px);
  }

  .metric-label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #fff;
  }

  .metric-sublabel {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`

const StatBadge = styled.span<{ $variant: 'success' | 'warning' | 'danger' | 'info' }>`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${(props) => {
    switch (props.$variant) {
      case 'success':
        return 'background: #10b981; color: white;'
      case 'warning':
        return 'background: #f59e0b; color: white;'
      case 'danger':
        return 'background: #ef4444; color: white;'
      case 'info':
      default:
        return 'background: #3b82f6; color: white;'
    }
  }}
`

const ClassRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;

  .class-name {
    flex: 1;
    color: #fff;
    font-weight: 500;
  }

  .class-count {
    margin: 0 1rem;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
  }

  .class-percentage {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
    min-width: 80px;
    text-align: right;
  }
`

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
  margin: 0 1rem;

  .progress-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`

interface DatasetClassAnalysisTabProps {
  datasetSource: string
  projectId: string
}

const DatasetClassAnalysisTab: React.FC<DatasetClassAnalysisTabProps> = ({
  datasetSource,
  projectId,
}) => {
  const [viewMode, setViewMode] = useState<'bar' | 'pie'>('bar')

  const { data: project } = useGetProjectQuery(projectId || '', {
    skip: !projectId,
  })

  const specificDatasetSources = useMemo(() => [datasetSource], [datasetSource])

  const classDistribution = useClassDistribution(
    projectId || '',
    project?.datasetid || '',
    specificDatasetSources
  )

  const { data: annoStats, isFetching: isFetchingStats } = useAnnotationStats(
    projectId || '',
    project?.datasetid || '',
    specificDatasetSources
  )

  // Get statistics for the specific dataset
  const datasetStats = annoStats?.statistics?.datasource_distributions?.[datasetSource]
  const totalImages = datasetStats?.images || 0
  const totalAnnotations = datasetStats?.annotations || 0
  const annotatedImages = annoStats?.statistics?.total_annotated_images || 0
  const nullAnnotations = annoStats?.statistics?.annotated_null_images || 0

  const totalClasses = useMemo(() => {
    // Use classDistribution array length if available (more accurate)
    if (classDistribution && classDistribution.length > 0) {
      return classDistribution.filter((item) => item.name !== 'Unknown' && item.name !== 'null')
        .length
    }
    // Fallback to annotations_per_class count
    return annoStats?.statistics?.annotations_per_class
      ? Object.keys(annoStats.statistics.annotations_per_class || {}).filter(
          (key) => key !== 'Unknown' && key !== 'null'
        ).length
      : 0
  }, [classDistribution, annoStats])

  // Calculate distribution statistics
  const distributionStats = useMemo(() => {
    if (!classDistribution || classDistribution.length === 0) return null

    const filtered = classDistribution.filter(
      (item) => item.name !== 'Unknown' && item.name !== 'null'
    )
    const values = filtered.map((item) => item.value)

    const mean = values.reduce((a, b) => a + b, 0) / values.length
    const max = Math.max(...values)
    const min = Math.min(...values)
    const median = [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)]
    const stdDev = Math.sqrt(
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length
    )
    const coefficientOfVariation = mean > 0 ? (stdDev / mean) * 100 : 0

    // Calculate class balance
    // Only check for imbalance if there are multiple classes (more than 1)
    let imbalancedClasses: typeof filtered = []
    let imbalancedCount = 0

    if (filtered.length > 1) {
      imbalancedClasses = filtered.filter((item) => {
        const percentage = (item.value / totalAnnotations) * 100
        return percentage < 1 || percentage > 70 // Very small or dominant classes
      })
      imbalancedCount = imbalancedClasses.length
    }

    return {
      mean,
      median,
      max,
      min,
      stdDev,
      coefficientOfVariation,
      imbalancedCount,
      imbalancedClasses,
      totalClasses: filtered.length,
    }
  }, [classDistribution, totalAnnotations])

  // Calculate balance score
  const balanceScore = useMemo(() => {
    if (!distributionStats) return null

    // Score based on coefficient of variation
    // Lower CV = more balanced
    // 0-20% = excellent, 20-40% = good, 40-60% = moderate, 60+ = poor
    const cv = distributionStats.coefficientOfVariation

    if (cv < 20) return { score: 'Excellent', color: 'success' as const, value: 100 - cv }
    if (cv < 40) return { score: 'Good', color: 'info' as const, value: 85 - (cv - 20) }
    if (cv < 60) return { score: 'Moderate', color: 'warning' as const, value: 70 - (cv - 40) }
    return { score: 'Poor', color: 'danger' as const, value: Math.max(20, 50 - (cv - 60)) }
  }, [distributionStats])

  const isLoadingStats = !annoStats || isFetchingStats

  if (isLoadingStats || !annoStats) {
    return (
      <ViewContainer>
        <ScrollableContent>
          <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div>Loading class distribution analysis...</div>
          </div>
        </ScrollableContent>
      </ViewContainer>
    )
  }

  return (
    <ViewContainer>
      <ScrollableContent>
        {/* Quick Summary */}
        <AnalysisCard>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1.5rem',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#fff',
                  marginBottom: '0.25rem',
                }}
              >
                {totalClasses}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Total Classes
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#fff',
                  marginBottom: '0.25rem',
                }}
              >
                {distributionStats?.mean
                  ? distributionStats.mean.toFixed(0)
                  : totalAnnotations > 0
                  ? (totalAnnotations / totalClasses).toFixed(0)
                  : '0'}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Avg per Class
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: '#fff',
                  marginBottom: '0.25rem',
                }}
              >
                {annotatedImages}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Annotated Images
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  color: nullAnnotations > 0 ? '#f59e0b' : '#10b981',
                  marginBottom: '0.25rem',
                }}
              >
                {nullAnnotations}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Null Annotations
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {nullAnnotations > 0 && (
              <div
                style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
                <AlertTriangle size={24} style={{ color: '#3b82f6', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#fff', marginBottom: '0.25rem' }}>
                    {nullAnnotations.toLocaleString()} null annotations
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                    {annotatedImages > 0
                      ? `${((nullAnnotations / annotatedImages) * 100).toFixed(
                          1
                        )}% of annotated images have no labels`
                      : 'These images have no annotations'}
                  </div>
                </div>
              </div>
            )}

            {distributionStats && distributionStats.imbalancedCount > 0 && (
              <div
                style={{
                  background: 'rgba(245, 158, 11, 0.1)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <AlertTriangle size={24} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#fff', marginBottom: '0.25rem' }}>
                      {distributionStats.imbalancedCount} imbalanced classes detected
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      Some classes have extreme distributions (&lt;1% or &gt;70% of annotations)
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                      Imbalanced Classes:
                    </strong>
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {distributionStats.imbalancedClasses.map((item, index) => {
                      const percentage = ((item.value / totalAnnotations) * 100).toFixed(1)
                      const isUnderrepresented = percentage < '1'
                      return (
                        <div
                          key={index}
                          style={{
                            padding: '0.5rem',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '4px',
                            marginBottom: '0.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div
                              style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: item.color,
                              }}
                            />
                            <span style={{ color: '#fff', fontSize: '0.875rem' }}>{item.name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span
                              style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}
                            >
                              {item.value.toLocaleString()} ({percentage}%)
                            </span>
                            <span
                              style={{
                                fontSize: '0.75rem',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '4px',
                                background: isUnderrepresented
                                  ? 'rgba(239, 68, 68, 0.2)'
                                  : 'rgba(245, 158, 11, 0.2)',
                                color: isUnderrepresented ? '#ef4444' : '#f59e0b',
                              }}
                            >
                              {isUnderrepresented ? 'Underrepresented' : 'Dominant'}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: '1.5',
                      }}
                    >
                      <strong style={{ color: '#fff' }}>Impact:</strong> Imbalanced classes can
                      cause the model to bias towards dominant classes and perform poorly on
                      underrepresented ones. Consider collecting more data for underrepresented
                      classes or applying data balancing techniques.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </AnalysisCard>

        {/* Full Class Distribution */}
        <AnalysisCard>
          <div className="card-header">
            <h3>Class Distribution</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setViewMode('bar')}
                style={{
                  padding: '0.5rem 1rem',
                  background: viewMode === 'bar' ? '#800085' : 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Bar
              </button>
              <button
                onClick={() => setViewMode('pie')}
                style={{
                  padding: '0.5rem 1rem',
                  background: viewMode === 'pie' ? '#800085' : 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Pie
              </button>
            </div>
          </div>
          <div>
            {viewMode === 'bar' ? (
              <div className="chart-container" style={{ minHeight: '250px' }}>
                <ChartWrapper style={{ alignItems: 'flex-end', height: '250px' }}>
                  {classDistribution
                    .filter((item) => item.name !== 'Unknown' && item.name !== 'null')
                    .map((item, index) => {
                      const barHeight = Math.max(
                        20,
                        (item.value / (distributionStats?.max || 1)) * 150
                      )
                      return (
                        <div
                          key={index}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            height: '100%',
                          }}
                        >
                          <div
                            style={{
                              flex: '1 1 auto',
                              minHeight: '150px',
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                width: '100%',
                                maxWidth: '40px',
                                background: item.color,
                                borderRadius: '4px 4px 0 0',
                                height: `${barHeight}px`,
                                transition: 'height 0.3s ease',
                              }}
                              title={`${item.name}: ${item.value}`}
                            />
                          </div>
                          <div
                            style={{
                              height: '60px',
                              flexShrink: 0,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              paddingTop: '0.5rem',
                              width: '100%',
                            }}
                          >
                            <div style={{ fontSize: '0.7rem', color: item.color, fontWeight: 600 }}>
                              {item.value}
                            </div>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                                textAlign: 'center',
                                wordBreak: 'break-word',
                                marginTop: '0.25rem',
                                maxHeight: '32px',
                                overflow: 'hidden',
                              }}
                            >
                              {item.name}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                </ChartWrapper>
              </div>
            ) : (
              <div
                style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.5)' }}
              >
                <PieChart size={64} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                <div>Pie chart visualization coming soon</div>
                <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Use the bar chart view for now
                </div>
              </div>
            )}
          </div>
        </AnalysisCard>

        {/* Detailed Class List */}
        <AnalysisCard>
          <div className="card-header">
            <h3>
              All Classes (
              {
                classDistribution.filter((item) => item.name !== 'Unknown' && item.name !== 'null')
                  .length
              }
              )
            </h3>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {classDistribution
              .filter((item) => item.name !== 'Unknown' && item.name !== 'null')
              .map((item, index) => {
                const percentage =
                  totalAnnotations > 0 ? ((item.value / totalAnnotations) * 100).toFixed(1) : '0'
                return (
                  <ClassRow key={index}>
                    <span className="class-name" style={{ color: item.color }}>
                      {item.name}
                    </span>
                    <span className="class-count">{item.value.toLocaleString()}</span>
                    <ProgressBar>
                      <div
                        className="progress-fill"
                        style={{
                          width: `${percentage}%`,
                          background: item.color,
                        }}
                      />
                    </ProgressBar>
                    <span className="class-percentage">{percentage}%</span>
                  </ClassRow>
                )
              })}
          </div>
        </AnalysisCard>
      </ScrollableContent>
    </ViewContainer>
  )
}

export default DatasetClassAnalysisTab
